"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import Logo from "@/components/Logo";

// ── Outline icons (inactive) ───────────────────────────────────────────
import { GlobeAmericasIcon as GlobeOutline }        from "@heroicons/react/24/outline";
import { SparklesIcon as SparklesOutline }          from "@heroicons/react/24/outline";
import { MapIcon as MapOutline }                    from "@heroicons/react/24/outline";
import { HomeIcon as HomeOutline }                  from "@heroicons/react/24/outline";
import { ViewfinderCircleIcon as TargetOutline }    from "@heroicons/react/24/outline";

// ── Solid icons (active) ───────────────────────────────────────────────
import { GlobeAmericasIcon as GlobeSolid }          from "@heroicons/react/24/solid";
import { SparklesIcon as SparklesSolid }            from "@heroicons/react/24/solid";
import { MapIcon as MapSolid }                      from "@heroicons/react/24/solid";
import { HomeIcon as HomeSolid }                    from "@heroicons/react/24/solid";
import { ViewfinderCircleIcon as TargetSolid }      from "@heroicons/react/24/solid";

const TABS = [
  {
    href:    "/home",
    label:   "Home",
    Outline: HomeOutline,
    Solid:   HomeSolid,
    match:   (p: string) => p.startsWith("/home"),
    badge:   false,
  },
  {
    href:    "/radar",
    label:   "Radar",
    Outline: GlobeOutline,
    Solid:   GlobeSolid,
    match:   (p: string) => p.startsWith("/radar"),
    badge:   false,
  },
  {
    // Pre-trip target lists — its own tab because it's used in the field
    href:    "/targets",
    label:   "Targets",
    Outline: TargetOutline,
    Solid:   TargetSolid,
    match:   (p: string) => p.startsWith("/targets"),
    badge:   false,
  },
  {
    // Trips + Create merged: /trips now leads with the trip list (search,
    // filters, stats) and each trip's detail page carries the "Create an
    // Instagram Carousel" CTA into /carousel?trip=... — still matched here
    // so the tab stays highlighted through that whole flow.
    href:    "/trips",
    label:   "Create",
    Outline: SparklesOutline,
    Solid:   SparklesSolid,
    match:   (p: string) => p.startsWith("/trips") || p.startsWith("/carousel"),
    badge:   false,
  },
];

interface AppNavProps {
  avatarUrl?: string | null;
  initials?: string;
}

function NavAvatar({
  avatarUrl,
  initials,
  size,
  active,
}: {
  avatarUrl?: string | null;
  initials?: string;
  size: number;
  active: boolean;
}) {
  if (avatarUrl) {
    return (
      <Image
        src={avatarUrl}
        alt="Profile"
        width={size}
        height={size}
        style={{
          borderRadius: "50%",
          border: `2px solid ${active ? "#2d8a5e" : "transparent"}`,
          objectFit: "cover",
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: active ? "#2d8a5e" : "#c8dccb",
      color: active ? "#fff" : "#4a6650",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: Math.round(size * 0.38), fontWeight: 900,
      border: `2px solid ${active ? "#2d8a5e" : "transparent"}`,
      flexShrink: 0,
    }}>
      {initials ?? "?"}
    </div>
  );
}

export default function AppNav({ avatarUrl, initials }: AppNavProps) {
  const pathname = usePathname();
  const profileActive =
    pathname.startsWith("/profile") ||
    pathname.startsWith("/share") ||
    pathname.startsWith("/checklist") ||
    pathname.startsWith("/chat") ||
    pathname.startsWith("/collection") ||
    pathname.startsWith("/bird") ||
    pathname.startsWith("/dashboard") ||
    pathname.startsWith("/season") ||
    pathname.startsWith("/alerts");

  return (
    <>
      {/* ── Mobile: fixed bottom tab bar ────────────────────── */}
      <div className="app-nav-mobile">
        {TABS.map(({ href, label, Outline, Solid, match, badge }) => {
          const active = match(pathname);
          const Icon = active ? Solid : Outline;
          return (
            <Link
              key={href}
              href={href}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px 0 6px",
                textDecoration: "none",
                color: active ? "#2d8a5e" : "#1a2b1d",
                gap: 2,
                transition: "color 0.15s",
              }}
            >
              <div style={{ position: "relative", width: 24, height: 24 }}>
                <Icon width={24} height={24} />
                {badge && !active && (
                  <div style={{
                    position: "absolute", top: -2, right: -3,
                    width: 7, height: 7, borderRadius: "50%",
                    background: "#e8453c",
                    border: "1.5px solid #f2f6f2",
                  }} />
                )}
              </div>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.3 }}>
                {label}
              </span>
            </Link>
          );
        })}

        {/* Profile — avatar */}
        <Link
          href="/profile"
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "8px 0 6px",
            textDecoration: "none",
            color: profileActive ? "#2d8a5e" : "#1a2b1d",
            gap: 2,
            transition: "color 0.15s",
          }}
        >
          <NavAvatar avatarUrl={avatarUrl} initials={initials} size={24} active={profileActive} />
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.3 }}>
            Me
          </span>
        </Link>
      </div>

      {/* ── Desktop: sticky top bar ──────────────────────────── */}
      <div className="app-nav-desktop">
        <Logo href="/home" style={{ marginRight: 32 }} />
        <div style={{ display: "flex", gap: 4, flex: 1, alignItems: "center" }}>
          {TABS.map(({ href, label, match, badge }) => {
            const active = match(pathname);
            return (
              <Link
                key={href}
                href={href}
                style={{
                  padding: "6px 14px",
                  borderRadius: 20,
                  textDecoration: "none",
                  fontSize: 14,
                  fontWeight: 700,
                  background: active ? "#e6f4ed" : "transparent",
                  color: active ? "#2d8a5e" : "#4a6650",
                  transition: "background 0.15s, color 0.15s",
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {label}
                {badge && !active && (
                  <div style={{
                    width: 7, height: 7, borderRadius: "50%",
                    background: "#e8453c", flexShrink: 0,
                  }} />
                )}
              </Link>
            );
          })}
        </div>

        {/* Profile avatar — desktop */}
        <Link
          href="/profile"
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "4px 10px 4px 4px",
            borderRadius: 20,
            textDecoration: "none",
            background: profileActive ? "#e6f4ed" : "transparent",
            transition: "background 0.15s",
          }}
        >
          <NavAvatar avatarUrl={avatarUrl} initials={initials} size={28} active={profileActive} />
          <span style={{
            fontSize: 14, fontWeight: 700,
            color: profileActive ? "#2d8a5e" : "#4a6650",
          }}>
            Profile
          </span>
        </Link>
      </div>
    </>
  );
}
